"use client";

import { useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import {
    ArrowUp,
    Bot,
    CalendarClock,
    Check,
    ChevronDown,
    Lock,
    Mic,
    Paperclip,
    Settings,
    Settings2,
    TriangleAlert,
    Users,
    X,
} from "lucide-react";
import { toast } from "sonner";

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAutoGrowTextarea } from "@/hooks/use-auto-grow-textarea";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCommandSession } from "@/components/aios-command";
import { useCommandPrototype } from "@/components/command-team-v2/prototype/store";
import { CommandComposerModelPicker } from "@/components/command-team-v2/composer/command-composer-model-picker";
import { CommandContextUsagePill } from "@/components/command-team-v2/composer/command-context-usage-ring";
import { pickLatestContextUsage } from "@/utils/command-context-usage";
import { PresetPopover } from "@/components/command-team-v2/chat/preset-popover";
import { CommandV2ComposerSchedulerDialog } from "@/components/command-team-v2/dialogs/command-v2-composer-scheduler-dialog";
import type { ProtoAttachedFile, ProtoPreset } from "@/components/command-team-v2/prototype/types";
import { useCommandAgentExpiredIntegrations } from "@/hooks/use-command-agent-expired-integrations";

interface CommandV2AssistantComposerProps {
    sessionId?: string | null;
    projectId?: string | null;
    onSent?: (sessionId: string | null) => void;
    onNewChat?: () => void;
}

export function CommandV2AssistantComposer({
    sessionId,
    projectId,
    onSent,
    onNewChat,
}: CommandV2AssistantComposerProps) {
    const router = useRouter();
    const { agentId } = useCommandSession();
    const {
        state,
        actions,
        activatingAgentId,
        hasMorePresets,
        isFetchingMorePresets,
        loadMorePresets,
        threadMessages,
        sessionIdForMessages,
        contextUsage: storeContextUsage,
        isTyping = false,
        streamingMessageId = null,
    } = useCommandPrototype();
    const [draft, setDraft] = useState("");
    const [docs, setDocs] = useState<(ProtoAttachedFile & { id: string })[]>([]);
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const selectedAgentId = state.activeAgentId ?? agentId;
    const sessionAgentId = sessionId
        ? state.sessions.find((session) => session.id === sessionId)?.agentId ?? null
        : null;
    const displayAgentId = sessionAgentId ?? selectedAgentId;
    const activeAgent = displayAgentId
        ? state.agents.find((agent) => agent.id === displayAgentId)
        : undefined;
    const { expiredNames } = useCommandAgentExpiredIntegrations(displayAgentId);
    const connectionIssues =
        expiredNames.length > 0 ? expiredNames : (activeAgent?.appIssues ?? []);
    const pickableAgents = state.agents
        .filter((agent) => agent.isActive)
        .sort((a, b) => a.name.localeCompare(b.name));
    const sessionCheckAgentId = sessionId
        ? (sessionAgentId ?? displayAgentId)
        : null;

    const send = (raw: string) => {
        const text = raw.trim();
        if (!text && docs.length === 0) return;
        // The chat ignores sends while a reply is still streaming (or reconnecting); keep the draft.
        if (isTyping || streamingMessageId) {
            toast.info("Command is still working on the last reply", {
                description: "Your message is kept. Send it once the reply finishes.",
            });
            return;
        }
        setDraft("");
        const attached = docs.map((file) => ({ name: file.name, size: file.size }));
        setDocs([]);
        const sentSessionId = actions.sendMessage(text, {
            attachedFiles: attached,
            sessionId,
            selectSession: false,
            projectId: projectId ?? null,
        });
        onSent?.(sentSessionId);
    };

    const handlePresetPick = (preset: ProtoPreset) => {
        if (preset.chain) {
            const sentSessionId = actions.sendMessage(
                preset.chain.steps[0]?.promptText ?? preset.promptText,
                {
                    presetId: preset.id,
                    chainStepIndex: 0,
                    sessionId,
                    selectSession: false,
                    projectId: projectId ?? null,
                }
            );
            onSent?.(sentSessionId);
            return;
        }
        setDraft(preset.promptText);
        textareaRef.current?.focus();
    };

    const composerHeight = useAutoGrowTextarea(textareaRef, draft);
    const contextUsage = useMemo(() => {
        if (sessionId && sessionIdForMessages && sessionId !== sessionIdForMessages) {
            const drawerMessages =
                state.sessions.find((session) => session.id === sessionId)?.messages ?? [];
            return pickLatestContextUsage(drawerMessages);
        }
        return storeContextUsage ?? pickLatestContextUsage(threadMessages ?? []);
    }, [sessionId, sessionIdForMessages, state.sessions, storeContextUsage, threadMessages]);

    return (
        <div className="shrink-0 px-3 pb-2 pt-1">
                    <div className="mb-1.5 ml-1 flex flex-wrap items-center gap-1.5">
                    <div className="inline-flex max-w-full items-center gap-1 rounded-full border bg-card py-1 pl-1.5 pr-1.5 text-xs font-medium transition-colors hover:border-primary/40">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    type="button"
                                    className="inline-flex min-w-0 items-center gap-1.5"
                                    aria-label="Choose the agent that answers"
                                >
                                    <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10">
                                        <Bot className="size-3 text-primary" />
                                    </span>
                                    <span className="truncate">
                                        {activeAgent?.name ?? "Choose an agent"}
                                    </span>
                                    <ChevronDown className="size-3 shrink-0 text-muted-foreground" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="start"
                                side="top"
                                className="app-scrollbar max-h-[min(34rem,var(--radix-dropdown-menu-content-available-height))] w-[280px] overflow-y-auto"
                            >
                                <DropdownMenuLabel className="flex items-center gap-2">
                                    <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10">
                                        <Bot className="size-3 text-primary" />
                                    </span>
                                    <span className="flex-1">Command agents</span>
                                    <button
                                        type="button"
                                        onClick={() => router.push("/workforce/command-team/agents")}
                                        className="flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                                        aria-label="Manage all Command agents"
                                    >
                                        <Settings2 className="size-3.5" />
                                    </button>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                {pickableAgents.length === 0 ? (
                                    <DropdownMenuItem disabled>No agents available</DropdownMenuItem>
                                ) : (
                                    pickableAgents.map((agent) => {
                                        const isTeam = (agent.visibility ?? "personal") === "team";
                                        const isActivating = activatingAgentId === agent.id;
                                        const isAnotherActivating =
                                            activatingAgentId != null &&
                                            activatingAgentId !== agent.id;
                                        return (
                                            <DropdownMenuItem
                                                key={agent.id}
                                                disabled={Boolean(isActivating || isAnotherActivating)}
                                                onSelect={(event) => {
                                                    event.preventDefault();
                                                    if (isActivating || isAnotherActivating) return;
                                                    actions.selectAgent(agent.id);
                                                }}
                                                className="group/agent gap-2 focus:bg-[var(--iai-purple-4)] focus:text-[var(--iai-navy)] data-[highlighted]:bg-[var(--iai-purple-4)] data-[highlighted]:text-[var(--iai-navy)] data-[highlighted]:[&_.agent-role]:text-[var(--iai-navy)]/70"
                                            >
                                                <span className="min-w-0 flex-1">
                                                    <span className="flex min-w-0 items-center gap-1.5 text-[13px] font-medium">
                                                        {isTeam ? (
                                                            <span title="Team agent" className="flex shrink-0 items-center">
                                                                <Users className="!size-3 !text-current" aria-label="Team agent" />
                                                            </span>
                                                        ) : (
                                                            <span title="Personal agent" className="flex shrink-0 items-center">
                                                                <Lock className="!size-3 !text-current" aria-label="Personal agent" />
                                                            </span>
                                                        )}
                                                        <span className="truncate">{agent.name}</span>
                                                        {agent.appIssues?.length ? (
                                                            <span
                                                                title={`Connection issue: ${agent.appIssues.join(", ")}`}
                                                                className="size-1.5 shrink-0 rounded-full bg-amber-500"
                                                            />
                                                        ) : null}
                                                    </span>
                                                    <span className="agent-role block truncate text-[11px] text-muted-foreground">
                                                        {agent.role}
                                                    </span>
                                                </span>
                                                {agent.id === state.activeAgentId ||
                                                (sessionCheckAgentId &&
                                                    agent.id === sessionCheckAgentId) ? (
                                                    <span className="flex shrink-0 items-center gap-1.5">
                                                        {agent.id === state.activeAgentId ? (
                                                            <span
                                                                title="Active agent"
                                                                className="size-2 shrink-0 rounded-full bg-emerald-500"
                                                                aria-label="Active agent"
                                                            />
                                                        ) : null}
                                                        {sessionCheckAgentId &&
                                                        agent.id === sessionCheckAgentId ? (
                                                            <Check
                                                                className="size-3.5 shrink-0 text-primary"
                                                                aria-label="Session agent"
                                                            />
                                                        ) : null}
                                                    </span>
                                                ) : null}
                                            </DropdownMenuItem>
                                        );
                                    })
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                        {activeAgent ? (
                            <button
                                type="button"
                                onClick={() =>
                                    router.push(
                                        `/workforce/command-team/agents/${activeAgent.id}?back=chat`
                                    )
                                }
                                aria-label={`Open settings for “${activeAgent.name}”`}
                                className="flex size-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                            >
                                <Settings className="size-3" />
                            </button>
                        ) : null}
                    </div>
                    {activeAgent && connectionIssues.length ? (
                        <button
                            type="button"
                            onClick={() =>
                                router.push(
                                    `/workforce/command-team/agents/${activeAgent.id}?panel=tools&back=chat`
                                )
                            }
                            className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700 transition-colors hover:bg-amber-100 dark:border-amber-700/50 dark:bg-amber-950/30 dark:text-amber-400 dark:hover:bg-amber-950/50"
                        >
                            <TriangleAlert className="size-3" />
                            Reconnect {connectionIssues.join(", ")}
                        </button>
                    ) : null}
                    </div>

            <div className="command-spin-outline rounded-3xl border bg-card px-6 py-4 shadow-sm">
                {docs.length > 0 ? (
                    <div className="mb-2 flex flex-wrap gap-1.5">
                        {docs.map((doc) => (
                            <span
                                key={doc.id}
                                className="inline-flex items-center gap-1.5 rounded-lg border bg-muted/50 px-2 py-1 text-xs"
                            >
                                <span className="max-w-[160px] truncate">{doc.name}</span>
                                <button
                                    type="button"
                                    onClick={() =>
                                        setDocs((current) => current.filter((item) => item.id !== doc.id))
                                    }
                                    aria-label={`Remove ${doc.name}`}
                                >
                                    <X className="size-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                ) : null}
                <ScrollArea className="w-full" style={{ height: composerHeight }}>
                    <Textarea
                        ref={textareaRef}
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" && !event.shiftKey) {
                                event.preventDefault();
                                send(draft);
                            }
                        }}
                        rows={2}
                        placeholder="Ask Command anything…"
                        className="block min-h-0 w-full resize-none overflow-y-hidden border-0 bg-transparent py-1 pl-0 pr-3 text-base leading-6 shadow-none focus-visible:ring-0 lg:text-sm"
                    />
                </ScrollArea>
                <div className="flex items-center justify-between">
                    <TooltipProvider delayDuration={400}>
                        <div className="flex items-center gap-0.5">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="flex size-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                        aria-label="Attach files"
                                    >
                                        <Paperclip className="size-4" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">
                                    Attach files
                                </TooltipContent>
                            </Tooltip>
                            <PresetPopover
                                compact
                                presets={state.presets}
                                onPick={handlePresetPick}
                                onToggleFavourite={actions.toggleFavourite}
                                onMovePreset={actions.movePreset}
                                onCreatePreset={actions.createPreset}
                                currentPrompt={draft}
                                hasNextPage={hasMorePresets}
                                isFetchingNextPage={isFetchingMorePresets}
                                onLoadMore={loadMorePresets}
                            />
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (!draft.trim()) {
                                                toast.error("Add a message to schedule");
                                                return;
                                            }
                                            setScheduleOpen(true);
                                        }}
                                        aria-label="Schedule a prompt"
                                        className="flex size-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                    >
                                        <CalendarClock className="size-4" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="text-xs">
                                    Schedule a prompt
                                </TooltipContent>
                            </Tooltip>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <CommandContextUsagePill
                                usage={contextUsage}
                                onNewChat={onNewChat ?? (() => actions.newChat(projectId ?? undefined))}
                            />
                            <CommandComposerModelPicker
                                agentId={activeAgent?.id ?? null}
                            />
                            {draft.trim() || docs.length > 0 ? (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button
                                            type="button"
                                            onClick={() => send(draft)}
                                            className="flex size-9 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-90"
                                            aria-label="Send"
                                        >
                                            <ArrowUp className="size-4" />
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="text-xs">
                                        Send
                                    </TooltipContent>
                                </Tooltip>
                            ) : (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                toast.info("Voice input isn't part of this demo yet", {
                                                    description: "Type your message instead.",
                                                })
                                            }
                                            className="flex size-9 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-blue-500 p-[1.5px]"
                                            aria-label="Voice input"
                                        >
                                            <span className="flex size-full items-center justify-center rounded-full bg-background">
                                                <Mic className="size-4 text-foreground" />
                                            </span>
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="text-xs">
                                        Voice input
                                    </TooltipContent>
                                </Tooltip>
                            )}
                        </div>
                    </TooltipProvider>
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.txt,.md,.rtf"
                    className="hidden"
                    onChange={(event) => {
                        const files = Array.from(event.target.files ?? []);
                        if (files.length === 0) return;
                        setDocs((current) => [
                            ...current,
                            ...files.map((file) => ({
                                id: `doc_${Math.random().toString(36).slice(2, 9)}`,
                                name: file.name,
                                size: file.size,
                            })),
                        ]);
                        event.target.value = "";
                    }}
                />
            </div>
            <p className="pb-1 pt-2 text-center text-xs text-muted-foreground">
                Command can make mistakes. Double-check replies.
            </p>

            <CommandV2ComposerSchedulerDialog
                open={scheduleOpen}
                onOpenChange={setScheduleOpen}
                prompt={draft.trim()}
                sessionId={sessionId}
                agentName={activeAgent?.name}
                onScheduled={() => {
                    setDraft("");
                    setDocs([]);
                }}
            />
        </div>
    );
}
